/*
 * The client's emoji vocabulary: emojibase-data 17.0.0 turned into the shape the ":" autocomplete
 * source and the picker both read. It is core's because core may not import a module
 * (architecture.md D21): the ":" trigger is the composer's own, and the picker serves a chat message
 * and a document's comment alike (DV S-12-D13) — one table, every reader, no copy.
 *
 * Two things decide the shape of this file.
 *
 * **It is never in a bundle that loads at boot.** `emojibase-data/en/compact.json` is 83 KB gzip
 * and the GitHub shortcodes another 32 KB, so both arrive through a dynamic `import()` inside
 * `loadEmojiTable()`. Vite makes that its own chunk; the app shell and the conversation route pay
 * nothing for it, and the first ":" or the first click on the picker is what fetches it.
 *
 * **The shortcodes must be the ones the server resolves.** `:tada:` is turned into 🎉 by
 * goldmark-emoji's GitHub definition set on the way in (internal/core/markdown/emoji.go),
 * and the server's own table is generated from `en/shortcodes/github.json` — so this file reads
 * exactly that preset. Any other one would make the composer promise a shortcode the message would
 * not resolve.
 */

/** Where a skin tone modifier lives in a hexcode; index 1 is the lightest of the five. */
const TONE_MODIFIERS = ["1F3FB", "1F3FC", "1F3FD", "1F3FE", "1F3FF"] as const

/** 0 is the neutral, tone-less form; 1 to 5 are the Fitzpatrick modifiers. */
export const MAX_SKIN_TONE = TONE_MODIFIERS.length

/**
 * The group emojibase calls "component" — skin-tone modifiers, hair pieces, regional indicators.
 * They are parts of other emoji, never an emoji a person picks, so the picker never shows them.
 */
const COMPONENT_GROUP = 2

/** One emoji as everything above it needs it: what to send, what to say, what to search on. */
export interface EmojiEntry {
	/** The neutral sequence, exactly as it goes over the wire and into the markdown. */
	char: string
	/** The CLDR name, English — an accessible name for a button and the search's last resort. */
	label: string
	/** GitHub shortcodes without the colons; the first is the canonical one. */
	shortcodes: readonly string[]
	/** Keywords emojibase ships; they are what makes ":party" find 🎉. */
	tags: readonly string[]
	/** The index into `EmojiTable.groups`. */
	group: number
	/** The Unicode ordering inside the group — the order the picker draws. */
	order: number
	/** The five toned sequences, by tone 1 to 5; empty when the emoji takes no tone. */
	tones: readonly string[]
}

/** One category tab of the picker. */
export interface EmojiGroup {
	/** emojibase's stable key — "smileys-emotion", "flags", … — which the copy keys are built on. */
	key: string
	/** The English label emojibase ships, for a group this build has no copy for. */
	fallbackLabel: string
	entries: readonly EmojiEntry[]
}

export interface EmojiTable {
	all: readonly EmojiEntry[]
	groups: readonly EmojiGroup[]
	/** The entry one shortcode resolves to, or undefined — the same answer the server gives. */
	byShortcode(code: string): EmojiEntry | undefined
	/** The entry a sequence belongs to, tone-folded, so a reaction chip can be named. */
	byChar(char: string): EmojiEntry | undefined
	/**
	 * The best matches for a term, most relevant first: a shortcode that starts with it beats a
	 * keyword that does, and both beat a word of the name.
	 */
	search(term: string, limit: number): EmojiEntry[]
}

/** The sequence to send for an entry at a tone; the neutral one when it takes none. */
export function withSkinTone(entry: EmojiEntry, tone: number): string {
	if (tone < 1 || tone > MAX_SKIN_TONE) return entry.char
	return entry.tones[tone - 1] ?? entry.char
}

/**
 * The characters a lookup folds away: the presentation selectors and the tone modifiers. A
 * keyboard decides whether it emits U+FE0F, and a reaction arrives with whatever it emitted — the
 * server folds the same way (internal/core/emoji: `byStripped`).
 */
const FOLDED = /\uFE0E|\uFE0F|[\u{1F3FB}-\u{1F3FF}]/gu

function fold(char: string): string {
	return char.replace(FOLDED, "")
}

interface CompactLike {
	group?: number | undefined
	hexcode: string
	label: string
	order?: number | undefined
	tags?: string[] | undefined
	unicode: string
	skins?: CompactLike[] | undefined
}

interface MessagesLike {
	groups: { key: string; message: string; order: number }[]
}

type ShortcodesLike = Record<string, string | string[]>

function shortcodesOf(map: ShortcodesLike, hexcode: string): string[] {
	const found = map[hexcode]
	if (found === undefined) return []
	return typeof found === "string" ? [found] : found
}

/** The toned sequences of one entry, by tone index — only the single-modifier ones. */
function tonesOf(emoji: CompactLike): string[] {
	const tones: string[] = []
	for (const modifier of TONE_MODIFIERS) {
		const skin = emoji.skins?.find((candidate) => {
			const parts = candidate.hexcode.split("-").filter((p) => TONE_MODIFIERS.includes(p as never))
			return parts.length === 1 && parts[0] === modifier
		})
		if (skin) tones.push(skin.unicode)
	}
	// A partial set would put the wrong tone under a swatch; all five or none.
	return tones.length === MAX_SKIN_TONE ? tones : []
}

/** Where a term matched, smallest number = shown first. */
function rank(entry: EmojiEntry, needle: string): number {
	for (const code of entry.shortcodes) {
		if (code === needle) return 0
		if (code.startsWith(needle)) return 1
	}
	for (const tag of entry.tags) {
		if (tag === needle) return 2
		if (tag.startsWith(needle)) return 3
	}
	const words = entry.label.toLowerCase().split(/[\s-]+/)
	if (words.some((word) => word.startsWith(needle))) return 4
	if (entry.label.toLowerCase().includes(needle)) return 5
	return Number.POSITIVE_INFINITY
}

export function buildEmojiTable(
	compact: readonly CompactLike[],
	shortcodes: ShortcodesLike,
	messages: MessagesLike,
): EmojiTable {
	const groupKeys = [...messages.groups].sort((a, b) => a.order - b.order)
	const all: EmojiEntry[] = []
	const byShortcode = new Map<string, EmojiEntry>()
	const byChar = new Map<string, EmojiEntry>()

	for (const emoji of compact) {
		if (emoji.group === undefined || emoji.group === COMPONENT_GROUP) continue
		const entry: EmojiEntry = {
			char: emoji.unicode,
			label: emoji.label,
			shortcodes: shortcodesOf(shortcodes, emoji.hexcode),
			tags: emoji.tags ?? [],
			group: emoji.group,
			order: emoji.order ?? 0,
			tones: tonesOf(emoji),
		}
		all.push(entry)
		for (const code of entry.shortcodes) if (!byShortcode.has(code)) byShortcode.set(code, entry)
		byChar.set(fold(entry.char), entry)
	}
	all.sort((a, b) => a.group - b.group || a.order - b.order)

	const groups: EmojiGroup[] = groupKeys
		.map((group) => ({
			key: group.key,
			fallbackLabel: group.message,
			entries: all.filter((entry) => entry.group === group.order),
		}))
		.filter((group) => group.entries.length > 0)

	return {
		all,
		groups,
		byShortcode: (code) => byShortcode.get(code.toLowerCase()),
		byChar: (char) => byChar.get(fold(char)),
		search(term, limit) {
			const needle = term.trim().toLowerCase()
			if (needle === "") return all.slice(0, limit)
			const hits: { entry: EmojiEntry; rank: number }[] = []
			for (const entry of all) {
				const score = rank(entry, needle)
				if (score !== Number.POSITIVE_INFINITY) hits.push({ entry, rank: score })
			}
			hits.sort(
				(a, b) => a.rank - b.rank || a.entry.group - b.entry.group || a.entry.order - b.entry.order,
			)
			return hits.slice(0, limit).map((hit) => hit.entry)
		},
	}
}

let pending: Promise<EmojiTable> | undefined
let loaded: EmojiTable | undefined

/**
 * The table, loaded once per session. The three data files arrive together because a picker with
 * names but no shortcodes, or the other way round, is not a usable picker.
 */
export function loadEmojiTable(): Promise<EmojiTable> {
	pending ??= Promise.all([
		import("emojibase-data/en/compact.json"),
		import("emojibase-data/en/shortcodes/github.json"),
		import("emojibase-data/en/messages.json"),
	]).then(([compact, shortcodes, messages]) => {
		// emojibase-data types its JSON through `emojibase`'s own interfaces. Two of the three are
		// already the shape this file reads; the emoji list is narrowed, because `CompactEmoji`
		// declares fields (`emoticon`) that nothing here looks at.
		loaded = buildEmojiTable(compact.default, shortcodes.default, messages.default)
		return loaded
	})
	return pending
}

/**
 * The table once `loadEmojiTable()` has settled, or nothing before that. A keystroke cannot wait
 * for a promise: the editor's `:shortcode:` rule resolves a code only when the table is already
 * here, and leaves the text for the server otherwise (F-2 §11.4).
 */
export function loadedEmojiTable(): EmojiTable | undefined {
	return loaded
}
